import UserService from './user.service';

/**
 * Single home-page API call for /home.
 *
 * Backend (get-home-contents) should return when include_notifications /
 * include_meetings_summary are sent in the POST body:
 *   - notifications: Notification[]  (respect notification_limit)
 *   - unread_notification_count: number (optional)
 *   - has_upcoming_meetings: boolean
 */
export function extractNotificationsFromHomeData(data) {
  if (!data) return [];
  if (Array.isArray(data.notifications)) return data.notifications;
  if (Array.isArray(data.notification_list)) return data.notification_list;
  if (data.notifications?.notifications) return data.notifications.notifications;
  return [];
}

export function countUnreadNotifications(notifications) {
  return notifications.filter((n) => !n.read_at).length;
}

export function extractHasUpcomingMeetings(data) {
  if (!data) return false;
  if (typeof data.has_upcoming_meetings === 'boolean') return data.has_upcoming_meetings;
  if (typeof data.upcoming_meetings_count === 'number') return data.upcoming_meetings_count > 0;
  if (Array.isArray(data.upcoming_meetings)) return data.upcoming_meetings.length > 0;
  if (Array.isArray(data.meetings?.upcoming)) return data.meetings.upcoming.length > 0;
  return false;
}

export function findLatestUnreadNotification(notifications) {
  const now = new Date();
  return notifications.find((item) => {
    if (item.read_at) return false;
    if (item.expires_at) {
      const expiresAt = new Date(item.expires_at);
      if (now >= expiresAt) return false;
    }
    return true;
  }) || null;
}

export function buildHomeBootstrapFromResponse(responseData) {
  const notifications = extractNotificationsFromHomeData(responseData);
  const unreadFromApi = responseData?.unread_notification_count;
  const unreadNotificationCount =
    typeof unreadFromApi === 'number'
      ? unreadFromApi
      : countUnreadNotifications(notifications);

  return {
    notifications,
    unreadNotificationCount,
    hasUpcomingMeetings: extractHasUpcomingMeetings(responseData),
    latestNotification: findLatestUnreadNotification(notifications),
  };
}

function homeResponseIncludesNotifications(data) {
  if (!data) return false;
  return (
    Object.prototype.hasOwnProperty.call(data, 'notifications') ||
    Object.prototype.hasOwnProperty.call(data, 'notification_list') ||
    Object.prototype.hasOwnProperty.call(data, 'unread_notification_count')
  );
}

async function fetchNotificationsFallback(limit = 10) {
  const response = await UserService.authGetRequest(`notifications/user?limit=${limit}`);
  if (response.data.status !== 'success') {
    return { notifications: [], unreadNotificationCount: 0 };
  }
  const notifications = response.data.data?.notifications || [];
  return {
    notifications,
    unreadNotificationCount: countUnreadNotifications(notifications),
  };
}

/**
 * One primary API call; uses bundled fields when backend supports them.
 * Meetings summary is not fetched here — only on the Meetings tab.
 * Falls back to notifications endpoint only if the home response omits that data.
 */
export async function fetchHomeContents() {
  const fcm = localStorage.getItem('fcm');
  const data = {
    include_notifications: true,
    include_meetings_summary: true,
    notification_limit: 10,
  };
  const response = await UserService.authPostRequest(`get-home-contents?fcm=${fcm}`, data);
  if (response.data.status !== 'success') {
    return response;
  }

  const payload = response.data.data || {};

  if (!homeResponseIncludesNotifications(payload)) {
    const result = await fetchNotificationsFallback(data.notification_limit);
    payload.notifications = result.notifications;
    payload.unread_notification_count = result.unreadNotificationCount;
  }

  return response;
}
